import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    GetById,
    Update,
    GetAllHallRoomTypes,
    GetAllPaymentMethods,
    GetAllFoodPackages,
    GetFoodPackageDetail,
    GetAllPFTL,
    GetAllFoods,
    CheckHallAvailability
} from "adminApi/HallBookingApi/HallBooking";
import { validateArrayForm } from "utils/validation";
import { HallBookingRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import EditHallBookingForm from "./Components/EditHallBookingForm";
import fields from "../json/HallRoomFeild.json"; // Your JSON file
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import { showError } from "utils/notifications";

// Match Add page createEmptyHall logic
const createEmptyHall = () => {
    const emptyHall = fields.formdata.RoomDetails.reduce((acc, field) => {
        acc[field.name] = "";
        return acc;
    }, {});
    return {
        ...emptyHall,
        selected_foods: [],
        food_price: 0,
        food_quantity: 0
    };
};

const globalFields = [
    ...fields.formdata.Details,
    ...fields.formdata.PaymentsDetails
];

const EditHallBooking = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { formdata } = fields;

    // Initial state setup directly from Add component pattern
    const initialValues = {
        ...globalFields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {}),
        halls: [createEmptyHall()],
        customer_id: "",
        customer_type: "existing"
    };

    const { values, errors, handleChange, handleArrayChange, setErrors, setValues } = useFormChange(
        initialValues,
        HallBookingRules
    );

    const halls = useMemo(() => values.halls || [], [values.halls]);

    const addHall = () => setValues(prev => ({ ...prev, halls: [...prev.halls, createEmptyHall()] }));
    const removeHall = (index) => setValues(prev => ({ ...prev, halls: prev.halls.length > 1 ? prev.halls.filter((_, i) => i !== index) : prev.halls }));
    const setHalls = (newHalls) => setValues(prev => ({ ...prev, halls: newHalls }));

    // Syncing billingRates and foodPackageDetails state names with Add page flow
    const [billingRates, setBillingRates] = useState({ bookingCharge: 0, foodCharge: 0, total: 0 });
    const [foodPackageDetails, setFoodPackageDetails] = useState({});
    const [loadingFetch, setLoadingFetch] = useState(true);

    // Fetch Options - Synchronized
    const { data: hallRoomTypes, loading: loadingHallRoomTypes } = useFetchAllData(GetAllHallRoomTypes);
    const { data: paymentMethods, loading: loadingPaymentMethods } = useFetchAllData(GetAllPaymentMethods);
    const { data: foodPackages, loading: loadingFoodPackages } = useFetchAllData(GetAllFoodPackages);
    const { data: pftlData, loading: loadingPFTL } = useFetchAllData(GetAllPFTL);
    const { data: allFoods, loading: loadingAllFoods } = useFetchAllData(GetAllFoods);

    // Hydrate existing data logic while keeping the flow intact
    useEffect(() => {
        const fetchBooking = async () => {
            try {
                const res = await GetById(id);
                if (res?.data?.success && res.data.data) {
                    const b = res.data.data;
                    const processedHalls = await Promise.all((b.halls || []).map(async h => {
                        const reconstructedFoods = h.foods ? h.foods.reduce((acc, f) => {
                            let entry = acc.find(sf => String(sf.food_type_id) === String(f.food_type_id));
                            if (!entry) { entry = { food_type_id: f.food_type_id, food_ids: [] }; acc.push(entry); }
                            entry.food_ids.push(f.food_id);
                            return acc;
                        }, []) : [];

                        if (h.food_package_id && !foodPackageDetails[h.food_package_id]) {
                            try {
                                const pkgRes = await GetFoodPackageDetail(h.food_package_id);
                                if (pkgRes.data?.success) {
                                    setFoodPackageDetails(prev => ({ ...prev, [String(h.food_package_id)]: pkgRes.data.data || pkgRes.data }));
                                }
                            } catch (e) { console.error(e); }
                        }
                        return { ...h, per_hour_rate: h.per_hour_rate || 0, selected_foods: reconstructedFoods };
                    }));

                    setValues({
                        ...b,
                        customer_id: b.customer_id,
                        customer_name: b.customer_name,
                        customer_phone: b.customer_phone,
                        customer_email: b.customer_email || "",
                        customer_type: "existing",
                        halls: processedHalls
                    });
                    setBillingRates({ bookingCharge: b.booking_charge || 0, foodCharge: b.food_charge || 0, total: b.total_amount || 0 });
                }
            } catch (err) { showError("Failed to load booking"); }
            finally { setLoadingFetch(false); }
        };
        if (id) fetchBooking();
    }, [id, setValues]);

    const handleHallChange = (index, field, value) => {
        handleArrayChange("halls", index, field, value);
        if (field === "hallroom_type_id") {
            const selectedType = hallRoomTypes?.find(t => String(t.id) === String(value));
            if (selectedType) handleArrayChange("halls", index, "capacity", selectedType.capacity || "");
            checkAvailability(index, value);
        }
        if (field === "food_package_id") {
            if (value) handleFoodPackageChange(index, value);
            else {
                handleArrayChange("halls", index, "food_price", "");
                handleArrayChange("halls", index, "food_quantity", "");
                handleArrayChange("halls", index, "selected_foods", []);
            }
        }
    };

    const checkAvailability = async (index, hallroomTypeId) => {
        if (!values.start_date || !values.end_date) return;
        try {
            const response = await CheckHallAvailability({ hallroom_type_id: hallroomTypeId, start_date: values.start_date, end_date: values.end_date });
            if (response.data?.success && !response.data.available) {
                showError(response.data.message || "Hall already booked for selected time");
                handleArrayChange("halls", index, "hallroom_type_id", "");
            }
        } catch (error) { console.error("Error checking availability:", error); }
    };

    const onMainChange = (e) => {
        const { name, value } = e.target;
        handleChange(e);
        if (name === "start_date" || name === "end_date") {
            const startDate = name === "start_date" ? value : values.start_date;
            const endDate = name === "end_date" ? value : values.end_date;
            if (startDate && endDate) {
                const startTime = new Date(startDate);
                const endTime = new Date(endDate);
                const diffHours = (endTime - startTime) / (1000 * 60 * 60);
                if (diffHours < 1) setErrors(prev => ({ ...prev, end_date: "Booking must be for a minimum of 1 hour" }));
                else setErrors(prev => { const next = { ...prev }; delete next.end_date; return next; });
            }
        }
    };

    const handleFoodPackageChange = async (hallIndex, packageId) => {
        if (!packageId) return;
        if (foodPackageDetails[packageId]) { updateHallWithPackageDetails(hallIndex, foodPackageDetails[packageId]); return; }
        try {
            const response = await GetFoodPackageDetail(packageId);
            if (response.data?.success) {
                const pkgDetail = response.data.data || response.data;
                setFoodPackageDetails(prev => ({ ...prev, [String(packageId)]: pkgDetail }));
                updateHallWithPackageDetails(hallIndex, pkgDetail);
            }
        } catch (error) { console.error(error); }
    };

    const updateHallWithPackageDetails = (hallIndex, packageDetail) => {
        if (!packageDetail) return;
        handleArrayChange("halls", hallIndex, "food_price", Number(packageDetail.package_price || 0));
        handleArrayChange("halls", hallIndex, "selected_foods", []);
        setValues(prev => {
            const nextHalls = [...prev.halls];
            if (nextHalls[hallIndex]) nextHalls[hallIndex] = { ...nextHalls[hallIndex], food_quantity: nextHalls[hallIndex].capacity || 0 };
            return { ...prev, halls: nextHalls };
        });
    };

    const calculateHours = (start, end) => {
        const diffMs = new Date(end) - new Date(start);
        return diffMs > 0 ? (diffMs / (1000 * 60 * 60)) : 0;
    };

    // EXACT mirror of AddHallBooking useEffect logic for charges
    useEffect(() => {
        if (loadingFetch) return;
        const hours = calculateHours(values.start_date, values.end_date);
        let bookingCharge = 0;
        let foodCharge = 0;
        let requiresHallUpdate = false;

        const nextHalls = halls.map(hall => {
            let nextHourlyRate = hall.hourly_rate;
            let nextPerHourRate = hall.per_hour_rate;
            if (hall.hallroom_type_id && hall.seatplan_id) {
                const selectedTypeObj = hallRoomTypes?.find(t => String(t.id) === String(hall.hallroom_type_id));
                const typeName = selectedTypeObj ? selectedTypeObj.hallroom_type : "";
                const matchingRateObj = hallRoomTypes?.find(t => t.hallroom_type === typeName && String(t.seatplan_id) === String(hall.seatplan_id));
                if (matchingRateObj) {
                    const perHourRate = Number(matchingRateObj.hourly_rate);
                    const calculatedTotalRent = hours * perHourRate;
                    if (Number(hall.per_hour_rate) !== perHourRate) { nextPerHourRate = perHourRate; requiresHallUpdate = true; }
                    if (Number(hall.hourly_rate) !== calculatedTotalRent) { nextHourlyRate = calculatedTotalRent; requiresHallUpdate = true; }
                }
            }
            if (String(hall.include_hall_rent) === "1") bookingCharge += Number(nextHourlyRate || 0);
            const qty = Number(hall.food_quantity) > 0 ? Number(hall.food_quantity) : Number(hall.capacity || 0);
            foodCharge += Number(hall.food_price || 0) * qty;
            return { ...hall, hourly_rate: nextHourlyRate, per_hour_rate: nextPerHourRate };
        });

        if (requiresHallUpdate) setValues(prev => ({ ...prev, halls: nextHalls }));
        else {
            const total = bookingCharge + foodCharge;
            setBillingRates(prev => (prev.total === total && prev.bookingCharge === bookingCharge ? prev : { bookingCharge, foodCharge, total }));
            setValues(prev => (Number(prev.total_amount) === total) ? prev : ({ ...prev, total_amount: total }));
        }
    }, [halls, values.start_date, values.end_date, hallRoomTypes, setValues, loadingFetch]);

    // FOLLOWING YOUR PRECISE CODE FLOW USING useFormSubmit (Copied from Add page)
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const finalData = { ...formValues, halls };
            const formData = new FormData();
            Object.entries(finalData).forEach(([key, value]) => {
                if (key === 'halls') formData.append(key, JSON.stringify(value));
                else if (!["customer_name", "customer_phone", "customer_email"].includes(key) && value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            });
            formData.append('booking_charge', billingRates.bookingCharge || 0);
            formData.append('food_charge', billingRates.foodCharge || 0);
            const total = Number(billingRates.total || 0);
            const paid = Number(formValues.paid_amount || 0);
            formData.append('booking_status', paid >= total && total > 0 ? 'confirmed' : (paid > 0 ? 'confirmed' : 'pending'));
            
            return await Update({ id, data: formData });
        },
        (vals) => {
            const allValues = { ...vals, halls };
            const err = validateArrayForm(allValues, HallBookingRules);
            if (vals.start_date && vals.end_date) {
                const diffHours = (new Date(vals.end_date) - new Date(vals.start_date)) / (1000 * 60 * 60);
                if (diffHours < 1) err.end_date = "Booking must be for a minimum of 1 hour";
            }
            setErrors(err);
            return err;
        },
        () => navigate(-1), // Specified callback from your example
        "Hall Booking updated successfully"
    );

    const isPageLoading = loadingSubmit || loadingHallRoomTypes || loadingPaymentMethods || loadingFoodPackages || loadingPFTL || loadingAllFoods || loadingFetch;

    return (
        <React.Fragment>
            {isPageLoading && <Spinner isSpinner={true} />}
            <EditHallBookingForm
                formdata={formdata}
                values={values}
                errors={errors}
                handleChange={onMainChange}
                handleArrayChange={handleArrayChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                isHallSectionEnabled={true}
                loading={loadingSubmit}
                setValues={setValues}
                hallRoomTypes={hallRoomTypes}
                paymentMethods={paymentMethods}
                foodPackages={foodPackages}
                foodPackageDetails={foodPackageDetails}
                pftlData={pftlData}
                allFoods={allFoods}
                billingRates={billingRates}
                halls={halls}
                setHalls={setHalls}
                addHall={addHall}
                removeHall={removeHall}
                handleHallChange={handleHallChange}
                isEditMode={true}
            />
        </React.Fragment>
    );
};

export default EditHallBooking;
